/**
 * Utilitaires pour la gestion des images et leur préchargement
 */

/**
 * Convertit une URL d'image en Data URL base64
 * Utile pour html2canvas qui ne peut pas toujours charger les images externes
 */
export const imageToBase64 = async (src: string): Promise<string> => {
  try {
    // Si c'est déjà une data URL, la retourner directement
    if (src.startsWith('data:')) {
      return src;
    }

    const response = await fetch(src);
    const blob = await response.blob();
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('Failed to convert image to base64:', src, error);
    return src; // Retourner l'original en cas d'échec
  }
};

/**
 * Précharge toutes les images d'un élément DOM
 */
export const preloadImagesInElement = async (element: HTMLElement): Promise<void> => {
  const images = element.querySelectorAll('img');
  const loadPromises = Array.from(images).map((img) => {
    if (img.complete && img.naturalHeight !== 0) return Promise.resolve();
    return new Promise<void>((resolve) => {
      img.onload = () => resolve();
      img.onerror = () => resolve(); // Ne pas bloquer si une image échoue
      // Forcer le rechargement
      if (img.src) {
        const currentSrc = img.src;
        img.src = '';
        img.src = currentSrc;
      }
    });
  });
  await Promise.all(loadPromises);
};

/**
 * Convertit toutes les images d'un élément cloné en base64 inline
 * Pour résoudre les problèmes de html2canvas avec les images externes
 */
export const inlineImagesInClone = async (clonedDoc: Document): Promise<void> => {
  const images = clonedDoc.querySelectorAll('img');
  
  const convertPromises = Array.from(images).map(async (img) => {
    try {
      if (!img.src || img.src.startsWith('data:')) return;
      
      const base64 = await imageToBase64(img.src);
      img.src = base64;
    } catch (error) {
      console.warn('Could not inline image:', img.src, error);
    }
  });
  
  await Promise.all(convertPromises);
};

/**
 * Charge le script html2canvas dans le window global si nécessaire pour Median
 * Median webScreenshot.shareElement nécessite window.html2canvas
 */
export const ensureHtml2canvasGlobal = async (): Promise<void> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (typeof (window as any).html2canvas !== 'undefined') return;
  
  // html2canvas est importé comme module, on doit l'exposer globalement pour Median
  const html2canvas = (await import('html2canvas')).default;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).html2canvas = html2canvas;
};
